import {useLazyQuery} from '@apollo/client'
import {GET_QUOTE_REPORTS_USERS} from '../../../gql/queries/reportsQuery'
import {IResQuoteReportsUsers} from '../../../types/reportsTypes'
import {useEffect} from 'react'
import {toast} from 'react-toastify'
import {defineMessages, useIntl} from 'react-intl'
import {Card, Spinner} from 'react-bootstrap'
import Chart from 'react-apexcharts'

const M = defineMessages({
  totalsTitle: {id: 'dashboard.quotes.chart.totalsTitle', defaultMessage: 'Total Quotes Summary'},
  usersTitle: {id: 'dashboard.quotes.chart.usersTitle', defaultMessage: 'Users'},
  seriesTotal: {id: 'common.field.total', defaultMessage: 'Total'},
  completed: {id: 'common.status.completed', defaultMessage: 'Completed'},
  pending: {id: 'common.status.pending', defaultMessage: 'Pending'},
  canceled: {id: 'common.status.canceled', defaultMessage: 'Canceled'},
  errorWithMessage: {id: 'common.error.withMessage', defaultMessage: 'Error: {message}'},
})

interface IQuoteProps {
  start: Date
  end: Date
}

export const QuoteReportUsers: React.FC<IQuoteProps> = ({end, start}) => {
  const intl = useIntl()
  const [gqlGet, {data, loading, error}] = useLazyQuery<IResQuoteReportsUsers>(
    GET_QUOTE_REPORTS_USERS,
    {fetchPolicy: 'no-cache'}
  )

  useEffect(() => {
    const variables = {
      startDate: start.toISOString(),
      endDate: end.toISOString(),
      pendingByDate: false,
      sortOrder: 'completed_desc',
    }
    gqlGet({
      variables,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start, end])

  useEffect(() => {
    if (error) toast.error(intl.formatMessage(M.errorWithMessage, {message: error.message}))
  }, [error, intl])

  if (!data || loading) {
    return (
      <div className='d-flex flex-center' style={{height: '35vh'}}>
        <Spinner animation='border' />
      </div>
    )
  }

  const {totals, userStats} = data.quoteStatus

  const statusLabels = {
    completed: intl.formatMessage(M.completed),
    pending: intl.formatMessage(M.pending),
    canceled: intl.formatMessage(M.canceled),
  }

  return (
    <div>
      <Card className='text-center mb-4'>
        <Card.Body>
          <Card.Title>{intl.formatMessage(M.totalsTitle)}</Card.Title>
          <Chart
            options={{
              chart: {
                type: 'bar',
              },
              labels: [statusLabels.completed, statusLabels.pending, statusLabels.canceled],
              dataLabels: {
                enabled: true,
              },
              colors: ['#28a745', '#ffc107', '#dc3545'],
            }}
            series={[
              {
                name: intl.formatMessage(M.seriesTotal),
                data: [
                  {
                    x: statusLabels.completed,
                    y: totals.totalCompleted,
                    fillColor: '#28a745',
                  },
                  {
                    x: statusLabels.pending,
                    y: totals.totalPending,
                    fillColor: '#ffc107',
                  },
                  {
                    x: statusLabels.canceled,
                    y: totals.totalCanceled,
                    fillColor: '#dc3545',
                  },
                ],
              },
            ]}
            type='bar'
            height={250}
          />
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>
          <Card.Title>{intl.formatMessage(M.usersTitle)}</Card.Title>
        </Card.Header>
        <Card.Body>
          <Chart
            options={{
              chart: {
                type: 'bar',
                height: 350,
                stacked: true,
                toolbar: {
                  show: true,
                },
                zoom: {
                  enabled: true,
                },
              },
              colors: ['#28a745', '#ffc107', '#dc3545'],
              responsive: [
                {
                  breakpoint: 480,
                  options: {
                    legend: {
                      position: 'bottom',
                      offsetX: -10,
                      offsetY: 0,
                    },
                  },
                },
              ],
              plotOptions: {
                bar: {
                  horizontal: false,
                },
              },
              xaxis: {
                categories: userStats.map((us) => `${us.user.firstName} ${us.user.lastName}`),
              },
            }}
            series={[
              {
                name: statusLabels.completed,
                data: userStats.map((us) => us.completedCount),
              },
              {
                name: statusLabels.pending,
                data: userStats.map((us) => us.pendingCount),
              },
              {
                name: statusLabels.canceled,
                data: userStats.map((us) => us.canceledCount),
              },
            ]}
            type='bar'
          />
        </Card.Body>
      </Card>
    </div>
  )
}
